<template>
  <v-card>
    <v-card-title class="mt-2 d-flex align-center text-wrap">
      <component
        :is="icon"
        v-if="icon"
        class="mr-2 text-secondary"
        size="30"
        stroke-width="1.5" />
      {{ title }}
    </v-card-title>
    <v-card-subtitle v-if="unitHint">{{ unitHint }}</v-card-subtitle>
    <v-card-text>
      <v-table
        class="location-matrix"
        density="comfortable">
        <thead>
          <tr>
            <td class="label-column" />
            <th
              v-for="(column, index) in columns"
              :key="column"
              :class="['text-right', { 'total-column': isTotalColumn(index) }]"
              scope="col">
              {{ column }}
            </th>
          </tr>
        </thead>
        <component
          :is="section.tag"
          v-for="section in sections"
          :key="section.tag">
          <tr
            v-for="row in section.rows"
            :key="row.key">
            <th
              class="label-column"
              scope="row">
              {{ row.label }}
            </th>
            <td
              v-for="(cell, index) in row.cells"
              :key="columns[index]"
              :class="['text-right', { 'total-column': isTotalColumn(index) }]">
              <v-skeleton-loader
                v-if="cell.isLoading"
                class="ml-auto"
                type="text"
                width="60" />
              <span
                v-else-if="cell.empty"
                class="text-disabled">
                –
              </span>
              <template v-else>
                <div class="cell-value">{{ cell.value }}</div>
                <div
                  v-if="cell.subText"
                  class="text-caption text-medium-emphasis">
                  {{ cell.subText }}
                </div>
              </template>
            </td>
          </tr>
        </component>
      </v-table>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { LocationMatrixCell, LocationMatrixRow, LocationMatrixTableProps } from '@/models/metricDefinitions';

const props = defineProps<LocationMatrixTableProps>();

const hasTotalColumn = computed(() => [...props.rows, props.total].some((row) => row?.total));

const MISSING_TOTAL: LocationMatrixCell = { value: '', isLoading: false, empty: true };

const columns = computed(() => (hasTotalColumn.value ? [...props.locations, 'Gesamt'] : props.locations));

const isTotalColumn = (index: number) => hasTotalColumn.value && index === columns.value.length - 1;

type DisplayRow = { key: string | number; label: string; cells: LocationMatrixCell[] };

// The row total becomes the last cell, so every row renders as one flat list of cells.
const toDisplayRow = (row: LocationMatrixRow): DisplayRow => ({
  key: row.key,
  label: row.label,
  cells: hasTotalColumn.value ? [...row.cells, row.total ?? MISSING_TOTAL] : [...row.cells]
});

// The total row goes into the footer, so both share one row template.
const sections = computed(() => [
  { tag: 'tbody', rows: props.rows.map(toDisplayRow) },
  ...(props.total ? [{ tag: 'tfoot', rows: [toDisplayRow(props.total)] }] : [])
]);
</script>

<style scoped>
.location-matrix th,
.location-matrix td {
  white-space: nowrap;
}

.location-matrix td {
  vertical-align: top;
  padding-block: 10px;
  font-variant-numeric: tabular-nums;
}

/* Keeps the category names in view while the locations scroll sideways on small screens */
.label-column {
  position: sticky;
  left: 0;
  z-index: 1;
  text-align: left;
  font-weight: 400;
  background: rgb(var(--v-theme-surface));
}

.cell-value {
  font-size: 1.125rem;
  font-weight: 500;
}

.total-column {
  border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-on-surface), 0.02);
}

/* Mirrors Vuetify's footer border selector with one more class, so the thicker line wins on specificity */
.location-matrix :deep(.v-table__wrapper) > table > tfoot > tr > * {
  border-top: 2px solid rgba(var(--v-theme-inputBorder), 0.5);
}

tfoot .label-column,
tfoot .cell-value {
  font-weight: 700;
}
</style>
